/**
 * Saga for the Preschool Curriculum admin slice (Phase 5).
 *
 * Mirrors spaceEntity saga exactly. The mapping table dispatches to the
 * right API call by entity name, using the generic handleFetch/handleUpdate
 * helpers.
 */
import { takeEvery, takeLatest } from 'redux-saga/effects'

import {
  fetchPreschoolSkill,
  createPreschoolSkill,
  updatePreschoolSkill,
  deletePreschoolSkill,
  fetchPreschoolWeeklyTheme,
  createPreschoolWeeklyTheme,
  updatePreschoolWeeklyTheme,
  deletePreschoolWeeklyTheme,
  fetchPreschoolWeeklySkillPlan,
  createPreschoolWeeklySkillPlan,
  updatePreschoolWeeklySkillPlan,
  deletePreschoolWeeklySkillPlan,
  fetchPreschoolMonthlyTheme,
  createPreschoolMonthlyTheme,
  updatePreschoolMonthlyTheme,
  deletePreschoolMonthlyTheme,
  fetchPreschoolAnnualLo,
  createPreschoolAnnualLo,
  updatePreschoolAnnualLo,
  deletePreschoolAnnualLo,
  fetchPreschoolSpiralLo,
  createPreschoolSpiralLo,
  updatePreschoolSpiralLo,
  deletePreschoolSpiralLo
} from 'api'
import constants from 'app/constants'
import {
  fetchPreschoolSkillData,
  fetchPreschoolSkillDataSuccess,
  fetchPreschoolWeeklyThemeData,
  fetchPreschoolWeeklyThemeDataSuccess,
  fetchPreschoolWeeklySkillPlanData,
  fetchPreschoolWeeklySkillPlanDataSuccess,
  fetchPreschoolMonthlyThemeData,
  fetchPreschoolMonthlyThemeDataSuccess,
  fetchPreschoolAnnualLoData,
  fetchPreschoolAnnualLoDataSuccess,
  fetchPreschoolSpiralLoData,
  fetchPreschoolSpiralLoDataSuccess,
  updatePreschoolDataSuccess
} from 'app/store/actions/preschoolEntity'
import * as types from 'app/store/types/preschoolEntity'

import { handleFetch, handleUpdate } from './saga.helper'

const mapping = {
  [constants.PRESCHOOL_SKILL]: {
    api: {
      fetch: fetchPreschoolSkill,
      create: createPreschoolSkill,
      update: updatePreschoolSkill,
      delete: deletePreschoolSkill
    },
    action: {
      fetch: fetchPreschoolSkillData,
      fetchSuccess: fetchPreschoolSkillDataSuccess
    }
  },
  [constants.PRESCHOOL_WEEKLY_THEME]: {
    api: {
      fetch: fetchPreschoolWeeklyTheme,
      create: createPreschoolWeeklyTheme,
      update: updatePreschoolWeeklyTheme,
      delete: deletePreschoolWeeklyTheme
    },
    action: {
      fetch: fetchPreschoolWeeklyThemeData,
      fetchSuccess: fetchPreschoolWeeklyThemeDataSuccess
    }
  },
  [constants.PRESCHOOL_WEEKLY_SKILL_PLAN]: {
    api: {
      fetch: fetchPreschoolWeeklySkillPlan,
      create: createPreschoolWeeklySkillPlan,
      update: updatePreschoolWeeklySkillPlan,
      delete: deletePreschoolWeeklySkillPlan
    },
    action: {
      fetch: fetchPreschoolWeeklySkillPlanData,
      fetchSuccess: fetchPreschoolWeeklySkillPlanDataSuccess
    }
  },
  [constants.PRESCHOOL_MONTHLY_THEME]: {
    api: {
      fetch: fetchPreschoolMonthlyTheme,
      create: createPreschoolMonthlyTheme,
      update: updatePreschoolMonthlyTheme,
      delete: deletePreschoolMonthlyTheme
    },
    action: {
      fetch: fetchPreschoolMonthlyThemeData,
      fetchSuccess: fetchPreschoolMonthlyThemeDataSuccess
    }
  },
  [constants.PRESCHOOL_ANNUAL_LO]: {
    api: {
      fetch: fetchPreschoolAnnualLo,
      create: createPreschoolAnnualLo,
      update: updatePreschoolAnnualLo,
      delete: deletePreschoolAnnualLo
    },
    action: {
      fetch: fetchPreschoolAnnualLoData,
      fetchSuccess: fetchPreschoolAnnualLoDataSuccess
    }
  },
  [constants.PRESCHOOL_SPIRAL_LO]: {
    api: {
      fetch: fetchPreschoolSpiralLo,
      create: createPreschoolSpiralLo,
      update: updatePreschoolSpiralLo,
      delete: deletePreschoolSpiralLo
    },
    action: {
      fetch: fetchPreschoolSpiralLoData,
      fetchSuccess: fetchPreschoolSpiralLoDataSuccess
    }
  }
}

export function* _fetchPreschoolEntity({ queries, entity } = {}) {
  const mappingData = mapping[entity]
  yield handleFetch(mappingData, queries, entity)
}

export function* _updatePreschoolEntity({ entity, event, value } = {}) {
  const mappingData = mapping[entity]
  yield handleUpdate(
    mappingData,
    entity,
    event,
    value,
    updatePreschoolDataSuccess
  )
}

export function* watchFetchPreschoolEntity() {
  yield takeLatest(
    [
      types.FETCH_PRESCHOOL_SKILL_DATA,
      types.FETCH_PRESCHOOL_WEEKLY_THEME_DATA,
      types.FETCH_PRESCHOOL_WEEKLY_SKILL_PLAN_DATA,
      types.FETCH_PRESCHOOL_MONTHLY_THEME_DATA,
      types.FETCH_PRESCHOOL_ANNUAL_LO_DATA,
      types.FETCH_PRESCHOOL_SPIRAL_LO_DATA
    ],
    _fetchPreschoolEntity
  )
}

export function* watchUpdatePreschoolEntity() {
  yield takeEvery(types.UPDATE_PRESCHOOL_DATA, _updatePreschoolEntity)
}
